<template>
    <router-view></router-view>
    <RefreshDialog></RefreshDialog>
    <InsightIframe v-if="hasInsightProfile"></InsightIframe>
    <ExperienceCreate v-if="experienceCreateData?.visible"></ExperienceCreate>
</template>

<script lang="ts">
import axios from 'axios';
import { updateFont } from '@/assets/i18n/index';
import { storeToRefs } from 'pinia';
import { defineComponent, getCurrentInstance, onMounted, onUnmounted, ref, watch } from 'vue';
import RefreshDialog from '@master/components/dialog/RefreshDialog.vue';
import InsightIframe from '@master/views/insight/InsightIframe.vue';
import ExperienceCreate from '@experience/components/create/ExperienceCreate.vue';
import { useCreateDataStore } from '@experience/store/experience.create.store';
import useEventBus from '@assets/plugins/eventBus';
import intercomService from '@assets/services/intercom.service';
import { useRoute, useRouter } from 'vue-router';
import { hasLoggedIn } from '@master/utils/account.util';
import { useStore } from 'vuex';
import { useNPPackageInfo } from '@models/package/package';
import analyticsService from '@assets/services/analytics.service';
import { useProfileInfo } from '@models/profile/useCurrentProfile';
import { useInsight } from '@master/hooks/service/useInsight';

export default defineComponent({
    name: 'App',
    setup() {
        const instance = getCurrentInstance();
        const route = useRoute();
        const router = useRouter();
        const store = useStore();
        const { profileCurrentProduct } = useProfileInfo();
        const { currentPackageInfo } = useNPPackageInfo();
        const { hasInsightProfile } = useInsight();
        const { eventBus } = useEventBus();
        const { experienceCreateData } = storeToRefs(useCreateDataStore());
        const logged = ref(hasLoggedIn());
        let pageloading: any = null;
        let pageVersionCheckTick: any = null;

        onMounted(() => {
            pageVersionCheckHandler();
            pageVersionCheckTick = setInterval(pageVersionCheckHandler, 1000 * 60 * 1);
            document.addEventListener('click', handleDocClick, false);
            eventBus.$on('upgrade', handleUpgradeClick, false);

            // 监听浏览器返回事件
            if (window.history && window.history.pushState) {
                history.pushState(null, null, document.URL);
                window.addEventListener('popstate', handlePopstate, false);
            }

            // 更新字体样式
            updateFont();
        });

        onUnmounted(() => {
            window.removeEventListener('popstate', handlePopstate, false);
            document.removeEventListener('click', handleDocClick, false);
            eventBus.$off('upgrade', handleUpgradeClick, false);
        });

        watch(
            () => store.state.master.main.visiblePageLoading,
            (flag: boolean) => {
                if (flag && router.currentRoute.value.name !== 'Login') {
                    pageloading = instance?.proxy.$loading({
                        lock: true,
                        width: '42px',
                        height: '42px',
                        background: 'transparent'
                    });
                } else {
                    pageloading?.close();
                }
            }
        );

        watch(
            () => route.name,
            (to, from) => {
                logged.value = hasLoggedIn();
            }
        );

        return {
            logged,
            hasInsightProfile,
            isLocal: window.location.host.startsWith('localhost'),
            experienceCreateData
        };

        function handlePopstate() {
            eventBus.$emit('popstate');
        }

        function handleUpgradeClick({
            type,
            params,
            gotoPrice = true
        }: {
            type: string;
            params?: string;
            gotoPrice: boolean;
        }) {
            gotoPrice && window.open(`/plan${params || ''}`);

            const {
                insight,
                insightIsGrowth,
                insightIsTrial,
                experience,
                experienceIsGrowth,
                experienceIsTrial
            } = currentPackageInfo.value || {};
            let current_plan = 'Free';
            if (insightIsGrowth && experienceIsGrowth) current_plan = 'Bundle Growth';
            else if (insightIsGrowth) current_plan = 'Insight Growth';
            else if (experienceIsGrowth) current_plan = 'Experience Growth';
            else if (insightIsTrial && experienceIsTrial) current_plan = 'Bundle Trial';
            else if (insightIsTrial) current_plan = 'Insight Trial';
            else if (experienceIsTrial) current_plan = 'Experience Trial';
            const current_plan_pv = [
                'Bundle Growth',
                'Insight Growth',
                'Insight Trial',
                'Free'
            ].includes(current_plan)
                ? insight?.privilege.monthlyPV
                : experience?.privilege.monthlyPV;

            analyticsService
                .usePTX('upgrade_click', {
                    product: profileCurrentProduct.value?.key === 'insight' ? 'pti' : 'ptx',
                    position: type,
                    current_plan,
                    current_plan_pv
                })
                .track();
        }

        async function pageVersionCheckHandler() {
            const response = await axios.get(
                `${window.location.protocol}//${window.location.host}${
                    import.meta.env.VITE_BASE_URL
                }index.html?r=${new Date().getTime()}`
            );
            const docElement = document.createElement('html');
            docElement.innerHTML = response.data;
            const newVersion = getMeta(docElement, 'version');
            const currentVersion = getMeta(document, 'version');
            if (newVersion && currentVersion && currentVersion !== newVersion) {
                eventBus.$emit('appVersionExpire');
                pageVersionCheckTick &&
                    (clearInterval(pageVersionCheckTick), (pageVersionCheckTick = null));
            }
        }

        function getMeta(doc: HTMLElement | Document, metaName: string) {
            const metas = doc.getElementsByTagName('meta');
            for (let i = 0; i < metas.length; i++) {
                if (metas[i].getAttribute('name') === metaName) {
                    return metas[i].getAttribute('content');
                }
            }
            return '';
        }

        function handleDocClick(e) {
            const classList = e.target.classList;
            classList && classList.contains('js_contact') && intercomService.contact();
        }
    },
    components: { RefreshDialog, InsightIframe, ExperienceCreate }
});
</script>

<style lang="scss" module>
@import '@style/import.scss';
:global {
    .js_contact:not(.pt-button) {
        @extend %link;
    }

    .el-loading-mask.is-fullscreen .el-loading-spinner .circular {
        width: 42px !important;
        height: 42px !important;
    }
}
</style>
